import React from 'react';
import { Search, BookOpen, Download, FileText, Calendar } from 'lucide-react';
import { Report } from '../../components/Reports/ReportCard';
import Loader from '../../components/Common/Loader';
import MobileReportListItem from '../../components/Reports/MobileReportListItem';

interface MobileReportsPageProps {
    reports: Report[];
    loading: boolean;
    error: string | null;
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    selectedType: string;
    setSelectedType: (t: string) => void;
    selectedYear: number;
    setSelectedYear: (y: number) => void;
    sortOrder: string;
    setSortOrder: (o: string) => void;
    onViewReport: (r: Report) => void;
    onDownloadReport: (r: Report) => void;
    downloadingGuide: boolean;
    onDownloadGuide: () => void;
}

const MobileReportsPage: React.FC<MobileReportsPageProps> = ({
    reports,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    selectedType,
    setSelectedType,
    selectedYear,
    setSelectedYear,
    sortOrder,
    setSortOrder,
    onViewReport,
    onDownloadReport,
    downloadingGuide,
    onDownloadGuide
}) => {

    // We filter the reports just like desktop parent does
    const filteredReports = reports.filter(r =>
        r.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const tabs = [
        { id: 'ALL', label: 'Tout' },
        { id: 'DAILY', label: 'Flash' },
        { id: 'WEEKLY', label: 'Hebdo' },
        { id: 'MONTHLY', label: 'Mensuel' },
        { id: 'QUARTERLY', label: 'Trimestriel' }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-main)' }}>

            {/* Top Section Wrapper (Search + Chips + Filters) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', zIndex: 10 }}>

                {/* Search Header */}
                <div style={{ position: 'relative', width: '100%' }}>
                    <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                        type="text"
                        placeholder="Rechercher un rapport..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%', padding: '12px 16px 12px 42px',
                            borderRadius: '16px', border: '1px solid var(--border-color)',
                            fontSize: '15px', outline: 'none', background: 'var(--bg-input)', color: 'var(--text-main)',
                            boxSizing: 'border-box',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    />
                </div>

                {/* Horizontal Scrollable Tabs (Chips) */}
                <div className="no-scroll-bar" style={{
                    display: 'flex',
                    gap: '10px',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    alignItems: 'center',
                    paddingBottom: '4px' // Room for chip shadow/border
                }}>
                    <style dangerouslySetInnerHTML={{ __html: `.no-scroll-bar::-webkit-scrollbar { display: none; }` }} />
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setSelectedType(tab.id)}
                                style={{
                                    flexShrink: 0,
                                    padding: '8px 20px',
                                    borderRadius: '24px',
                                    background: selectedType === tab.id ? '#3b82f6' : 'var(--bg-main)',
                                    border: '1px solid',
                                    borderColor: selectedType === tab.id ? '#3b82f6' : 'var(--border-color)',
                                    color: selectedType === tab.id ? '#fff' : 'var(--text-main)',
                                    fontWeight: selectedType === tab.id ? '600' : '500',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    touchAction: 'manipulation',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 'normal',
                                    boxShadow: selectedType === tab.id ? '0 2px 4px rgba(59,130,246,0.2)' : 'none'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Filters (Year & Sort) - Compact Row */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <Calendar size={14} color="var(--text-muted)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            style={{
                                width: '100%', padding: '12px 12px 12px 34px', borderRadius: '12px',
                                border: '1px solid var(--border-color)', fontSize: '13px',
                                background: 'var(--bg-main)', color: 'var(--text-main)', appearance: 'none',
                                boxSizing: 'border-box'
                            }}
                        >
                            <option value={2026}>2026</option>
                            <option value={2025}>2025</option>
                            <option value={2024}>2024</option>
                            <option value={0}>Toutes années</option>
                        </select>
                    </div>
                    <div style={{ flex: 1 }}>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            style={{
                                width: '100%', padding: '12px', borderRadius: '12px',
                                border: '1px solid var(--border-color)', fontSize: '13px',
                                background: 'var(--bg-main)', color: 'var(--text-main)', appearance: 'none',
                                textAlign: 'center',
                                boxSizing: 'border-box'
                            }}
                        >
                            <option value="desc">↓ Récent d'abord</option>
                            <option value="asc">↑ Ancien d'abord</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div style={{ padding: '0 16px 24px', flex: 1 }}>
                
                {/* Section Nos Guides (Mobile) */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                    borderRadius: '16px',
                    padding: '16px',
                    marginBottom: '20px',
                    border: '1px solid rgba(99, 102, 241, 0.15)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <BookOpen size={18} color="#6366f1" />
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text-heading)' }}>
                            Nos Guides d'Investissement 📚
                        </h3>
                    </div>
                    <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                        Téléchargez notre guide exclusif pour maîtriser les indicateurs techniques sur la BRVM.
                    </p>
                    <button
                        onClick={onDownloadGuide}
                        disabled={downloadingGuide}
                        style={{
                            width: '100%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px 14px',
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: downloadingGuide ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: downloadingGuide ? 0.8 : 1
                        }}
                    >
                        {downloadingGuide ? '📥 Téléchargement...' : '⬇ Guide Indicateurs Techniques (PDF)'}
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                        <Loader />
                    </div>
                ) : error ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px' }}>
                        {error}
                    </div>
                ) : filteredReports.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'var(--bg-box)', borderRadius: '16px' }}>
                        <BookOpen size={40} style={{ opacity: 0.3, marginBottom: '16px' }} />
                        <p style={{ margin: 0, fontSize: '15px' }}>Aucun rapport trouvé.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Total count */}
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', paddingLeft: '4px' }}>
                            {filteredReports.length} {filteredReports.length > 1 ? 'rapports trouvés' : 'rapport trouvé'}
                        </div>

                        {/* List of reports */}
                        {filteredReports.map(report => (
                            <MobileReportListItem
                                key={report.id}
                                report={report}
                                onView={() => onViewReport(report)}
                                onDownload={() => onDownloadReport(report)}
                            />
                        ))}
                    </div>
                )}
            </div>

        </div>
    );
};

export default MobileReportsPage;
